import React from 'react'
import { graphql } from 'gatsby'
import Layout from '../components/Layout/Layout'
import Hero from '../components/Hero/Hero'
import ButtonVariantEnum from '../enums/ButtonVariant'
import SideNavigation, { SideNavTypeEnum } from '../components/SideNavigation/SideNavigation'
import useScreenOrientation from '../hooks/useScreenOrientation'
import Seo from '../components/Seo/Seo'
import BlockFinancialResult from '../components/BlockFinancialResult/BlockFinancialResult'
import Heading from '../components/Heading/Heading'
import HeadingSizeEnum from '../enums/headingSize'
import CardContact from '../components/CardContact/CardContact'

import styled from 'styled-components'
import pxToRem from '../utils/pxToRem'
import Button from '../components/Button/Button'
import { deviceMax } from '../theme/breakpoints'
import { MainContainer } from '../theme/mainContainer.styled'
import Event from '../components/Event/Event'
import FinancialVariantEnum from '../enums/FinancialVariantEnum'
import ElementFinancial from '../components/ElementFinancial/ElementFinancial'
import LastestNews from '../components/LastestNews/LastestNews'
import NotFoundPage from './404'
import useCtaLink from '../hooks/useCtaLink'
import useMediaQuery from '../hooks/useMediaQuery'
import HeroVariantEnum from '../enums/HeroVariant'
import HeroNewHeader from '../components/HeroNewHeader/HeroNewHeader'

const StyledHeaderContainer = styled.div`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  display: none;
  @media ${deviceMax.mobileL} {
    display: flex;
    flex-direction: column;
    margin-right: 0;
    margin-bottom: 0;
  }
`

const StyledMainLayout = styled(MainContainer)`
  display: flex;
  justify-content: center;

  @media ${deviceMax.tablet} {
    flex-direction: column;
    margin: 0;
    max-width: 100%;
  }
`

const StyledLeftSide = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;
  max-width: ${pxToRem(384)};
  margin-right: ${pxToRem(20)};
  gap: ${pxToRem(20)};
  @media ${deviceMax.mobileL} {
    max-width: none;
    margin: ${pxToRem(10)};
  }
`

const StyledRightSide = styled.div`
  position: relative;
  width: 100%;
  margin: auto;
  max-width: ${pxToRem(792)};
`

const StyledContentData = styled.div`
  flex-direction: row;
  flex-wrap: wrap;
  display: flex;
  justify-content: space-between;
  gap: ${pxToRem(20)};
  @media ${deviceMax.tablet} {
    justify-content: center;
    padding: ${pxToRem(20)} ${pxToRem(10)};
    background: ${({ theme }) => theme.colors.unique.black};
  }
`

const StyledEventsContent = styled.div`
  justify-content: center;
  align-items: center;
  flex-direction: column;
  display: flex;
  width: 100%;
  margin: 0;
  padding: ${pxToRem(30)};
  gap: ${pxToRem(20)};
  background: ${({ theme }) => theme.colors.unique.white};
  @media ${deviceMax.tablet} {
    padding: ${pxToRem(10)};
    align-items: flex-start;
  }
`

const StyledEventsContainer = styled(MainContainer)`
  padding: 0;
  justify-content: center;
  align-items: center;
  display: flex;
  gap: ${pxToRem(20)};
  @media ${deviceMax.tablet} {
    flex-direction: column;
  }
`

const AnnualReportsContainer = styled.div`
  width: 100%;
  max-width: ${pxToRem(385)};

  @media ${deviceMax.laptop} {
    max-width: ${pxToRem(792)};
  }
`

const StyledBlockFinancialResult = styled(BlockFinancialResult)`
  width: 100%;
  max-width: ${pxToRem(385)};

  @media ${deviceMax.laptop} {
    max-width: ${pxToRem(792)};
  }
`

const LastestFinancialResultsContainer = styled.div`
  width: 100%;
  height: fit-content;
  max-width: ${pxToRem(385)};
  padding: ${pxToRem(20)};
  gap: ${pxToRem(20)};
  border-radius: ${pxToRem(5)};
  background: ${({ theme }) => theme.colors.unique.white};

  @media ${deviceMax.laptop} {
    max-width: ${pxToRem(792)};
  }
`

const LastestFinancialTitle = styled.p`
  margin-bottom: ${pxToRem(10)};
  font-size: ${({ theme }) => theme.fonts.sizes.titleH2.laptop};
  line-height: ${({ theme }) => theme.fonts.lineHeight.titleH2};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
`

export const StyledCardContact = styled.div`
  @media ${deviceMax.mobileL} {
    margin: 0 ${pxToRem(10)};
  }
`

const StyledButton = styled.div`
  display: flex;
  width: 100%;
  @media ${deviceMax.mobileL} {
    justify-content: center;
  }
`

const StyledButtonCTA = styled(Button)`
  width: 100%;
  justify-content: center;
`

const ContactsCardsContainerDesktop = styled.div`
  display: block;
  @media ${deviceMax.mobileL} {
    display: none;
  }
`

const ContactsCardsContainerMobile = styled.div`
  display: none;
  @media ${deviceMax.mobileL} {
    display: block;
  }
`

const PORTRAIT_PRIMARY = 0
const TARGET = '_blank'

const Investors = ({ data, location }) => {
  const isMobile = useMediaQuery(deviceMax.mobileL)
  const isTablet = useMediaQuery(deviceMax.tablet)
  const screenOrientation = useScreenOrientation()

  if (data?.contentfulPageInvestors === null) {
    return <NotFoundPage location={location} />
  }

  const {
    pageTitle,
    imageHeaderDesktop,
    imageHeaderMobile,
    backgroundColor,
    contact,
    latestNewsBlocTitle,
    financialResultsBlocTitle,
    latestFinancialResults,
    annualReports,
    eventsBlocTitle,
    events,
    ctaButton,
    seoMetaDescription,
    header,
  } = data.contentfulPageInvestors


  const [ctaUrl, ctaSlug] =
    ctaButton && ctaButton.url ? useCtaLink(ctaButton.url) : [null, null]

  const showSideNavigation = !(
    isMobile ||
    (isTablet && screenOrientation === PORTRAIT_PRIMARY)
  )

  const news = data?.allContentfulTemplateArticle.edges

  const contactsCards =
    contact &&
    contact.map((contact) => {
      return (
        <StyledCardContact key={contact.id}>
          <CardContact
            department={contact.department}
            email={contact.email}
            firstname={contact.firstname}
            key={contact.id}
            lastname={contact.lastname}
            phone={contact.phone}
          />
        </StyledCardContact>
      )
    })

  return (
    <Layout>
      <Seo
        title={pageTitle}
        description={seoMetaDescription?.seoMetaDescription}
        canonicalHref={location.pathname}
      />
      {header?.sys?.contentType?.sys?.id === 'basicHeader' || header?.sys?.contentType?.sys?.id === 'headerFullMedia' ? (
        <HeroNewHeader
          header={header}
          title={pageTitle}
          pathname={location.pathname}
          variant={HeroVariantEnum.investor}
        />
      ) : (
        <Hero
          mediaDesktop={imageHeaderDesktop}
          mediaMobile={imageHeaderMobile}
          title={pageTitle}
          backgroundColor={backgroundColor}
          pathname={location.pathname}
          variant={HeroVariantEnum.investor}
        />
      )}
      <StyledMainLayout>
        <StyledLeftSide>
          {ctaButton && (
            <StyledButton>
              <StyledButtonCTA
                to={ctaSlug}
                href={ctaUrl}
                variant={ButtonVariantEnum.primary}
                target={ctaUrl ? TARGET : null}
              >
                {ctaButton.label}
              </StyledButtonCTA>
            </StyledButton>
          )}
          {showSideNavigation && (
            <SideNavigation variant={SideNavTypeEnum.investor} />
          )}
          <ContactsCardsContainerDesktop>
            {contactsCards}
          </ContactsCardsContainerDesktop>
        </StyledLeftSide>
        <StyledRightSide>
          <LastestNews title={latestNewsBlocTitle} news={news} />
          <ContactsCardsContainerMobile>
            {contactsCards}
          </ContactsCardsContainerMobile>
          <StyledContentData>
            {latestFinancialResults && (
              <LastestFinancialResultsContainer>
                <LastestFinancialTitle>
                  {financialResultsBlocTitle}
                </LastestFinancialTitle>
                <ElementFinancial
                  section={latestFinancialResults[0]}
                  variant={FinancialVariantEnum.investor}
                />
              </LastestFinancialResultsContainer>
            )}
            {annualReports && (
              <AnnualReportsContainer>
                {annualReports.map((block, index) => (
                  <StyledBlockFinancialResult
                    key={index}
                    block={block}
                    variant={FinancialVariantEnum.investor}
                  />
                ))}
              </AnnualReportsContainer>
            )}
          </StyledContentData>
        </StyledRightSide>
      </StyledMainLayout>
      {events && (
        <StyledEventsContent>
          <Heading label={eventsBlocTitle} size={HeadingSizeEnum.h2} />
          <StyledEventsContainer>
            {events.map((eventCard, index) => (
              <Event
                key={index}
                eventCard={eventCard}
                index={index}
                isMobile={isMobile}
              />
            ))}
          </StyledEventsContainer>
        </StyledEventsContent>
      )}
    </Layout>
  )
}

export default Investors

export const investors = graphql`
    query Investors {
        contentfulPageInvestors {
            slug
            pageTitle
            seoMetaDescription {
                seoMetaDescription
            }
            header {
              ...basicHeaderFragment
              ...headerFullMediaFragment
            }
            id
            createdAt
            backgroundColor
            ctaButton {
                url
                label
            }
            imageHeaderDesktop {
                ...imageHeaderFragment
            }
            imageHeaderMobile {
                ...imageHeaderFragment
            }
            contact {
                ...contactFragment
            }
            latestNewsBlocTitle
            eventsBlocTitle
            events {
                date
                endDate
                title
                ctaButton {
                    label
                    url
                }
            }
            seoMetaDescription {
                seoMetaDescription
            }
            financialResultsBlocTitle
            latestFinancialResults {
                blocTitle
                documents {
                    ...documentFragment
                }
            }
            annualReports {
                blocTitle
                blocDescription {
                    blocDescription
                }
                financialTabs {
                    tabTitle
                    contents {
                        ... on ContentfulComponentFinancialDocumentsBloc {
                            blocTitle
                            documents {
                                ...documentFragment
                            }
                            sections {
                                blocTitle
                                documents {
                                    ...documentFragment
                                }
                            }
                        }
                    }
                }
            }
        }
        allContentfulTemplateArticle(
            sort: {date: DESC}
            filter: {node_locale: {eq: "en-US"}, categories: {elemMatch: {name: {eq: "Finance"}}}}
            limit: 2
        ) {
            edges {
                node {
                    id
                    date
                    title
                    backgroundColor
                    imageHeaderDesktop {
                        ...imageHeaderFragment
                    }
                    imageHeaderMobile {
                        ...imageHeaderFragment
                    }
                    thumbnail {
                        name
                        altText {
                            altText
                        }
                        cloudinaryAsset {
                            resource_type
                            format
                            url
                            context {
                                custom {
                                    alt
                                    caption
                                }
                            }
                        }
                        contentfulAsset {
                            file {
                                url
                            }
                            gatsbyImageData
                        }
                    }
                    slug
                    categories {
                        name
                        sys {
                            type
                            revision
                            contentType {
                                sys {
                                    id
                                    linkType
                                    type
                                }
                            }
                        }
                    }
                    seoMetaDescription {
                        seoMetaDescription
                    }
                    documentsList {
                        ...documentFragment
                    }
                }
            }
        }
    }

`
